/**
 * LearningStreakChart — Shows daily learning activity over the last 30 days.
 *
 * Expects: data = [{ date: "2026-05-01", minutes: 45, sessions: 2 }, ...]
 * Fallback: renders a visual "streak heatmap" grid if data is sparse.
 */
import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import dayjs from "utils/datetime";

// Format raw minutes (may be fractional) into a clean label: "1h 5m" / "12m" / "<1m".
const fmtMinutes = (raw) => {
  if (!raw || raw <= 0) return "0m";
  if (raw < 1) return "<1m";
  const total = Math.round(raw);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${total}m`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  // Read the real watch-time off the datum (the bar height may be a floored
  // display value), so the tooltip always shows the honest number.
  const datum = payload[0]?.payload || {};
  return (
    <Box
      sx={{
        background: "rgba(10,10,10,0.92)",
        backdropFilter: "blur(16px)",
        border: "1px solid color-mix(in srgb, var(--text-primary) 10%, transparent)",
        borderRadius: "10px",
        px: 2,
        py: 1.5,
      }}
    >
      <Typography
        sx={{
          color: "color-mix(in srgb, var(--text-primary) 50%, transparent)",
          fontSize: "0.68rem",
          fontFamily: "'Inter', sans-serif",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#a78bfa", fontFamily: "'Space Grotesk', sans-serif" }}>
        {fmtMinutes(datum.minutes)}
      </Typography>
      {datum.sessions > 0 && (
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "color-mix(in srgb, var(--text-primary) 45%, transparent)", fontFamily: "'Inter', sans-serif" }}>
          {datum.sessions} {datum.sessions === 1 ? "session" : "sessions"}
        </Typography>
      )}
    </Box>
  );
};

const LearningStreakChart = ({ data = [], streak = 0, totalTime = 0, height = 160 }) => {
  const { t } = useTranslation();

  // Format data for the last 14 days (more visual than 30 in a small space)
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) {
      // Generate empty placeholder data
      return Array.from({ length: 14 }, (_, i) => ({
        day: dayjs().subtract(13 - i, "days").format("dd"),
        date: dayjs().subtract(13 - i, "days").format("D MMM"),
        minutes: 0,
      }));
    }

    // Map the last 14 days of real data
    const dataMap = {};
    data.forEach((d) => {
      const key = dayjs(d.date).format("YYYY-MM-DD");
      dataMap[key] = d;
    });

    return Array.from({ length: 14 }, (_, i) => {
      const date = dayjs().subtract(13 - i, "days");
      const key = date.format("YYYY-MM-DD");
      const entry = dataMap[key];
      const minutes = entry?.minutes ?? entry?.timeSpent ?? entry?.duration ?? 0;
      return {
        day: date.format("dd"),
        date: date.format("D MMM"),
        minutes,
        sessions: entry?.sessions ?? entry?.activitiesCount ?? 0,
        active: !!entry?.hasActivity || minutes > 0,
      };
    });
  }, [data]);

  const hasActivity = chartData.some((d) => d.minutes > 0 || d.active);

  // Bar height uses a `display` value so an active day with ~0 watch-minutes
  // (e.g. an access ping) still renders a visible stub instead of a 0px bar.
  // The tooltip reads the real `minutes`, so the honest number is preserved.
  const displayData = React.useMemo(() => {
    const peak = Math.max(...chartData.map((d) => d.minutes), 0);
    const floor = peak > 0 ? Math.max(peak * 0.06, 1) : 1; // small visible stub
    return chartData.map((d) => ({
      ...d,
      display: d.minutes > 0 ? d.minutes : d.active ? floor : 0,
    }));
  }, [chartData]);
  const rawMinutes = totalTime || chartData.reduce((sum, d) => sum + d.minutes, 0);
  const totalMinutes = Math.round(rawMinutes);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  // Robust label: never show fractional minutes; sub-minute activity → "<1m"
  const timeLabel =
    hours > 0 ? `${hours}h ${mins}m` : totalMinutes > 0 ? `${totalMinutes}m` : "<1m";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25 }}
    >
      <Box
        sx={{
          p: 3,
          borderRadius: "16px",
          background: "color-mix(in srgb, var(--text-primary) 2%, transparent)",
          border: "1px solid color-mix(in srgb, var(--text-primary) 5%, transparent)",
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 600,
                color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
                fontFamily: "'Inter', sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 0.5,
              }}
            >
              {t("dashboard.learning_activity", "Learning Activity")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              {rawMinutes > 0 ? (
                <Typography
                  sx={{
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "var(--text-primary)",
                    fontFamily: "'Space Grotesk', sans-serif",
                    lineHeight: 1,
                  }}
                >
                  {timeLabel}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "color-mix(in srgb, var(--text-primary) 30%, transparent)",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  —
                </Typography>
              )}
            </Stack>
          </Box>
          {streak > 0 && (
            <Box
              sx={{
                px: 1.2,
                py: 0.5,
                borderRadius: "8px",
                background: "rgba(245,158,11,0.1)",
                border: "1px solid rgba(245,158,11,0.15)",
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography sx={{ fontSize: "0.9rem" }}>🔥</Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#f59e0b",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {streak} {t("dashboard.day_streak", "day streak")}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>

        {/* Chart — only when there is real daily activity */}
        {hasActivity ? (
          <Box sx={{ width: "100%", height }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={displayData} margin={{ top: 5, right: 0, left: -30, bottom: 0 }}>
                <defs>
                  <linearGradient id="streakBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" />
                    <stop offset="100%" stopColor="#d946ef" />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="color-mix(in srgb, var(--text-primary) 4%, transparent)"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "color-mix(in srgb, var(--text-primary) 25%, transparent)",
                    fontSize: 10,
                    fontFamily: "'Inter', sans-serif",
                  }}
                  dy={5}
                />
                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fill: "color-mix(in srgb, var(--text-primary) 20%, transparent)",
                    fontSize: 9,
                    fontFamily: "'Inter', sans-serif",
                  }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "color-mix(in srgb, var(--text-primary) 3%, transparent)" }} />
                <Bar
                  dataKey="display"
                  fill="url(#streakBar)"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={18}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          /* Clean empty state — faint baseline + message, no confusing empty axes */
          <Box
            sx={{
              width: "100%",
              height,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              borderRadius: "12px",
              border: "1px dashed color-mix(in srgb, var(--text-primary) 8%, transparent)",
              background:
                "linear-gradient(180deg, transparent, color-mix(in srgb, var(--text-primary) 2%, transparent))",
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ opacity: 0.35 }}>
              {[10, 18, 8, 22, 14, 26, 12].map((h, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: h,
                    borderRadius: "3px 3px 0 0",
                    background: "color-mix(in srgb, var(--text-primary) 18%, transparent)",
                  }}
                />
              ))}
            </Stack>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "0.72rem",
                color: "color-mix(in srgb, var(--text-primary) 35%, transparent)",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {t("dashboard.no_activity_yet", "Start learning to see your activity here")}
            </Typography>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default LearningStreakChart;
